/**
 * PricingPanel — tab "Pricing & Intratność" w TenderDetail.
 *
 * 5 sekcji w hierarchii informacji:
 *   1. ProfitabilityHero       — score 0-100 + verdict + EV
 *   2. BidRecommendation       — breakeven/min/target + what-if slider marży
 *   3. CostStackTable          — 8 kategorii kosztów + breakdown labor
 *   4. BoQItemsTable           — pozycje z SWZ + pokrycie ofert
 *   5. RFQThreadsTable         — komunikacja per dostawca + actions
 *
 * Workflow:
 *   1. operator klika "Wyciągnij BoQ" (jeśli brak)
 *   2. "Wybierz dostawców" → top 3-5 per kategoria
 *   3. "Generuj RFQ" → tworzy threads (drafted)
 *   4. operator: Approve → Send (per thread)
 *   5. czeka na odpowiedzi (auto przez Inbox Watcher) lub klik "Pobierz inbox"
 *   6. po quotes: Bid Optimizer → Cost Stack → Profitability ☑
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import {
  TrendingUp, AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw,
  FileText, Send, Eye, Mail, Phone, Inbox, Calculator, Target,
  Package, Users, DollarSign, Zap, ArrowUpRight, ChevronDown, ChevronUp,
  Sparkles, FlaskConical, AlertCircle, Info,
} from 'lucide-react';

import {
  extractTenderBoq, getTenderBoq,
  generateTenderRfq, listRfqThreads, approveRfqThread, sendRfqThread,
  getRfqThreadPdfUrl, pollRfqInbox,
  getTenderBidMix, getTenderCostStack, getTenderProfitability,
  getChannelCapabilities, planOutreach, executeOutreachStep,
} from '../../lib/api';

// ============================================================
// Helpers
// ============================================================

const fmtPLN = (v) => {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency', currency: 'PLN', maximumFractionDigits: 0,
  }).format(v);
};

const fmtPct = (v, digits = 1) => v == null || isNaN(v) ? '—' : `${Number(v).toFixed(digits)}%`;

const VERDICT_STYLES = {
  strongly_recommended: { color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/30', icon: CheckCircle2 },
  recommended:          { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/25', icon: CheckCircle2 },
  marginal:             { color: 'text-amber-400',   bg: 'bg-amber-500/10  border-amber-500/30',  icon: AlertTriangle },
  skip:                 { color: 'text-red-400',     bg: 'bg-red-500/10    border-red-500/30',    icon: XCircle },
};

const THREAD_STATUS_STYLES = {
  drafted:           { label: 'Draft',           color: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  approved:          { label: 'Zatwierdzony',    color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  sent:              { label: 'Wysłany',         color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  responded:         { label: 'Odpowiedział',    color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  quote_received:    { label: '✓ Oferta',        color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  negotiating:       { label: 'Negocjacje',      color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  closed_won:        { label: 'Wygrana',         color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  closed_lost:       { label: 'Przegrana',       color: 'bg-red-500/15 text-red-400 border-red-500/30' },
  closed_no_response:{ label: 'Brak odp.',       color: 'bg-slate-500/15 text-slate-500 border-slate-500/30' },
  send_failed:       { label: '⚠ Błąd wysyłki', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const COST_LABELS = {
  materials:           'Materiały (z RFQ)',
  labor:               'Robocizna',
  transport:           'Transport',
  warehouse:           'Magazynowanie',
  overhead:            'Koszty pośrednie',
  office:              'Biuro / Admin',
  wadium_capital_cost: 'Wadium (koszt kapitału)',
  kary_reserve:        'Rezerwa na kary umowne',
  warranty_reserve:    'Rezerwa serwisowa (gwarancja)',
};

// ============================================================
// 1. ProfitabilityHero
// ============================================================

function ProfitabilityHero({ profit, loading, onRefresh }) {
  if (!profit && !loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <div className="text-sm">Profitability nie obliczona</div>
          <div className="text-xs mt-1">Najpierw potrzebujesz BoQ + ofert dostawców (RFQ)</div>
          <Button size="sm" className="mt-4" onClick={onRefresh}>
            <Calculator className="w-4 h-4 mr-2" /> Oblicz profitability
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Liczę profitability...
        </CardContent>
      </Card>
    );
  }

  const score = profit?.profitability_score ?? 0;
  const verdict = profit?.verdict || 'marginal';
  const v = VERDICT_STYLES[verdict] || VERDICT_STYLES.marginal;
  const Icon = v.icon;
  const m = profit?.metrics || {};

  return (
    <Card className={`border-2 ${v.bg}`}>
      <CardContent className="py-5">
        <div className="flex items-start gap-6">
          {/* Score circle */}
          <div className="flex-shrink-0 text-center">
            <div className={`text-6xl font-bold ${v.color}`}>{Math.round(score)}</div>
            <div className="text-xs text-muted-foreground">/100</div>
            <Progress value={score} className="w-24 h-1.5 mt-2" />
          </div>

          {/* Verdict + EV */}
          <div className="flex-1 min-w-0">
            <div className={`flex items-center gap-2 text-lg font-semibold ${v.color}`}>
              <Icon className="w-5 h-5" />
              {profit?.verdict_label || verdict}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              EV (przy {fmtPct(m.win_rate_pct, 0)} win rate) ={' '}
              <span className={`font-medium ${m.ev_at_target_pln >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {m.ev_at_target_pln >= 0 ? '+' : ''}{fmtPLN(m.ev_at_target_pln)}
              </span>
            </div>
            {profit?.reasoning?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                {profit.reasoning.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <Info className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
                    {r}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Sensitivity */}
          <div className="flex-shrink-0 hidden md:block min-w-[180px]">
            <div className="text-[10px] uppercase text-muted-foreground mb-1">Sensitivity (EV vs WR)</div>
            <div className="space-y-0.5">
              {(profit?.sensitivity_table || []).map(s => (
                <div key={s.win_rate_pct} className="flex justify-between text-xs">
                  <span className="text-muted-foreground">WR {s.win_rate_pct}%:</span>
                  <span className={s.is_positive ? 'text-emerald-400' : 'text-red-400'}>
                    {s.is_positive ? '+' : ''}{fmtPLN(s.ev_pln)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button size="sm" variant="ghost" className="mt-4 ml-auto block" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Odśwież
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// 2. BidRecommendation (z what-if slider marży)
// ============================================================

function BidRecommendation({ costStack, loading, onRecalc }) {
  const [marginPct, setMarginPct] = useState(costStack?.target_margin_pct || 12);
  useEffect(() => {
    if (costStack?.target_margin_pct) setMarginPct(costStack.target_margin_pct);
  }, [costStack?.target_margin_pct]);

  if (!costStack || loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Target className="w-4 h-4" /> Rekomendacja oferty
          </CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Najpierw oblicz Cost Stack'}
        </CardContent>
      </Card>
    );
  }

  const cost = costStack.total_cost_pln || 0;
  const whatIfBid = cost * (1 + marginPct / 100);
  const whatIfBidVat = whatIfBid * 1.23;
  const whatIfProfit = whatIfBid - cost;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Target className="w-4 h-4" /> Rekomendacja oferty
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Breakeven</div>
            <div className="font-mono">{fmtPLN(costStack.breakeven_bid_pln)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Min margin ({fmtPct(costStack.min_margin_pct, 0)})</div>
            <div className="font-mono">{fmtPLN(costStack.min_margin_bid_pln)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Target ({fmtPct(costStack.target_margin_pct, 0)})</div>
            <div className="font-mono text-emerald-400 font-semibold">{fmtPLN(costStack.target_bid_pln)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-muted-foreground">Z VAT 23%</div>
            <div className="font-mono">{fmtPLN(costStack.target_bid_with_vat_pln)}</div>
          </div>
        </div>

        <div className="pt-3 border-t">
          <div className="text-xs font-medium mb-2 flex items-center justify-between">
            <span><Zap className="w-3 h-3 inline mr-1" /> What-if: marża {fmtPct(marginPct, 1)}</span>
            <span className="text-emerald-400 font-semibold">Profit: {fmtPLN(whatIfProfit)}</span>
          </div>
          <input
            type="range" min={5} max={30} step={0.5} value={marginPct}
            onChange={(e) => setMarginPct(Number(e.target.value))}
            className="w-full accent-emerald-500"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>5%</span>
            <span className="font-medium">Bid: {fmtPLN(whatIfBid)} (z VAT {fmtPLN(whatIfBidVat)})</span>
            <span>30%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// 3. CostStackTable
// ============================================================

function CostStackTable({ costStack, loading, onRefresh }) {
  if (!costStack && !loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-muted-foreground text-sm">
          Cost Stack nie obliczone — kliknij <Button size="sm" className="ml-2" onClick={onRefresh}>
            <Calculator className="w-3 h-3 mr-1" /> Oblicz
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (loading) return <Card><CardContent className="py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></CardContent></Card>;

  const stack = costStack.cost_stack || {};
  const total = costStack.total_cost_pln || 1;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Cost Stack ({fmtPLN(total)})
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {Object.entries(stack).map(([key, amount]) => {
            const pct = (amount / total) * 100;
            return (
              <div key={key} className="text-xs">
                <div className="flex justify-between mb-0.5">
                  <span className="text-muted-foreground">{COST_LABELS[key] || key}</span>
                  <span className="font-mono">
                    {fmtPLN(amount)} <span className="text-muted-foreground">({fmtPct(pct)})</span>
                  </span>
                </div>
                <div className="h-1 bg-muted rounded overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        {costStack.risk_assessment && (
          <div className="mt-3 pt-3 border-t text-xs flex items-center gap-2">
            <AlertCircle className={`w-3.5 h-3.5 ${
              costStack.risk_assessment.kary_severity === 'high' ? 'text-red-400' :
              costStack.risk_assessment.kary_severity === 'medium' ? 'text-amber-400' : 'text-emerald-400'
            }`} />
            <span className="text-muted-foreground">
              Ryzyko kar: <span className="font-medium">{costStack.risk_assessment.kary_severity}</span>{' '}
              (red flags score: {costStack.risk_assessment.red_flags_score})
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// 4. BoQItemsTable
// ============================================================

function BoQItemsTable({ boq, bidMix, loading, onExtract }) {
  if (!boq && !loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <Package className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-40" />
          <div className="text-sm text-muted-foreground mb-3">BoQ nie wyciągnięte z SWZ</div>
          <Button size="sm" onClick={onExtract}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Wyciągnij BoQ (LLM)
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (loading) return <Card><CardContent className="py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></CardContent></Card>;

  const items = boq?.items || [];
  const mixMap = bidMix?.mix || {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Package className="w-4 h-4" /> BoQ — {items.length} pozycji
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={onExtract}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-extract
          </Button>
        </div>
        {boq?.summary?.tender_type && (
          <CardDescription className="text-xs">Typ: {boq.summary.tender_type}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 px-1">#</th>
                <th className="text-left px-1">Opis</th>
                <th className="text-right px-1">Ilość</th>
                <th className="text-left px-1">Jedn.</th>
                <th className="text-left px-1">Kategoria</th>
                <th className="text-right px-1">Pokrycie</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                const m = mixMap[item.item_id];
                const hasMix = !!m;
                return (
                  <tr key={item.item_id || i} className="border-b border-border/30 hover:bg-muted/20">
                    <td className="py-1.5 px-1 text-muted-foreground">{item.line_number || i + 1}</td>
                    <td className="px-1">
                      <div className="line-clamp-2">{item.description}</div>
                      {hasMix && (
                        <div className="text-[10px] text-emerald-400 mt-0.5">
                          → {m.selected_supplier?.supplier_name} @ {fmtPLN(m.selected_supplier?.unit_price_pln)}/{item.unit}
                        </div>
                      )}
                    </td>
                    <td className="text-right px-1 font-mono">{item.quantity}</td>
                    <td className="px-1 text-muted-foreground">{item.unit}</td>
                    <td className="px-1">
                      <Badge variant="outline" className="text-[10px]">
                        {item.category_hint || item.category || '—'}
                      </Badge>
                    </td>
                    <td className="text-right px-1">
                      {hasMix ? (
                        <span className="text-emerald-400">✓ {m.offers_count} of.</span>
                      ) : (
                        <span className="text-muted-foreground">— brak</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {bidMix && (
          <div className="mt-3 pt-3 border-t text-xs flex items-center justify-between text-muted-foreground">
            <span>Pokrycie: <span className="font-medium">{fmtPct(bidMix.coverage_pct)}</span></span>
            <span>Dostawców w mix: <span className="font-medium">{bidMix.suppliers_count}</span></span>
            <span>Mix total: <span className="font-medium">{fmtPLN(bidMix.total_net_pln)}</span></span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// 5. RFQThreadsTable
// ============================================================

function ChannelsBadge({ channels }) {
  if (!channels || channels.length === 0) return <span className="text-muted-foreground text-xs">—</span>;
  const ICONS = { email: '✉️', sms: '📱', whatsapp: '💬', voice: '☎️', linkedin: '💼' };
  const seen = new Set();
  return (
    <div className="flex gap-0.5">
      {channels.map((c, i) => {
        const t = c.type || c.channel || c;
        if (seen.has(t)) return null;
        seen.add(t);
        return <span key={i} title={t} className="text-sm">{ICONS[t] || '?'}</span>;
      })}
    </div>
  );
}

function ChannelsCapabilitiesCard({ caps, loading }) {
  if (loading) return <Card><CardContent className="py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></CardContent></Card>;
  if (!caps) return null;

  const ICONS = { email: '✉️', sms: '📱', whatsapp: '💬', voice: '☎️', linkedin: '💼' };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Zap className="w-4 h-4" /> Dostępne kanały outbound
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          {Object.entries(caps).map(([ch, info]) => (
            <div key={ch} className={`p-2 rounded border ${info.available ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-500/20 bg-slate-500/5 opacity-60'}`}>
              <div className="text-base">{ICONS[ch] || '?'} {ch.toUpperCase()}</div>
              <div className="text-[10px] text-muted-foreground">
                {info.available ? '✓ Dostępne' : `⏳ Faza ${info.phase || '?'}`}
              </div>
              {info.dry_run && <div className="text-[9px] text-amber-400 mt-0.5">DRY-RUN</div>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RFQThreadsTable({ threads, loading, onApprove, onSend, onPreviewPDF, onPollInbox, onPlanOutreach }) {
  if (!threads || threads.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-muted-foreground text-sm">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Brak threadów RFQ — najpierw wygeneruj RFQ
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4" /> Komunikacja z dostawcami ({threads.length})
          </CardTitle>
          <Button size="sm" variant="outline" onClick={onPollInbox}>
            <Inbox className="w-3.5 h-3.5 mr-1" /> Sprawdź inbox
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-muted-foreground border-b">
              <tr>
                <th className="text-left py-1.5 px-1">Dostawca</th>
                <th className="text-left px-1">Tier</th>
                <th className="text-left px-1">Email</th>
                <th className="text-left px-1">Status</th>
                <th className="text-left px-1">Kanały użyte</th>
                <th className="text-right px-1">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {threads.map((t) => {
                const sty = THREAD_STATUS_STYLES[t.status] || { label: t.status, color: 'bg-slate-500/15 text-slate-400 border-slate-500/30' };
                const canApprove = ['drafted', 'pending_approval'].includes(t.status);
                const canSend = ['approved', 'drafted'].includes(t.status);
                return (
                  <tr key={t.id} className="border-b border-border/30 hover:bg-muted/20">
                    <td className="py-1.5 px-1">
                      <div className="font-medium">{t.supplier_name || t.supplier_id?.substring(0, 8)}</div>
                    </td>
                    <td className="px-1">
                      <Badge variant="outline" className="text-[10px]">T{t.supplier_tier ?? '?'}</Badge>
                    </td>
                    <td className="px-1 text-muted-foreground truncate max-w-[180px]">
                      {t.supplier_email || '—'}
                    </td>
                    <td className="px-1">
                      <Badge variant="outline" className={`text-[10px] ${sty.color}`}>{sty.label}</Badge>
                    </td>
                    <td className="px-1">
                      <ChannelsBadge channels={t.channels} />
                    </td>
                    <td className="px-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2"
                                onClick={() => onPreviewPDF(t.id)} title="Podgląd PDF">
                          <Eye className="w-3 h-3" />
                        </Button>
                        {canApprove && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-emerald-400"
                                  onClick={() => onApprove(t.id)} title="Zatwierdź">
                            <CheckCircle2 className="w-3 h-3" />
                          </Button>
                        )}
                        {canSend && (
                          <Button size="sm" className="h-6 px-2"
                                  onClick={() => onSend(t.id)} title="Wyślij email RFQ">
                            <Send className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-amber-400"
                                onClick={() => onPlanOutreach(t.id)} title="Multi-channel plan">
                          <Zap className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// MAIN — PricingPanel
// ============================================================

export default function PricingPanel({ tenderId }) {
  const [boq, setBoq] = useState(null);
  const [boqLoading, setBoqLoading] = useState(false);
  const [bidMix, setBidMix] = useState(null);
  const [costStack, setCostStack] = useState(null);
  const [profit, setProfit] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [channelCaps, setChannelCaps] = useState(null);
  const [outreachPlan, setOutreachPlan] = useState(null);  // {threadId, plan}
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Fetch BoQ
  const fetchBoQ = useCallback(async () => {
    try {
      const data = await getTenderBoq(tenderId);
      setBoq(data);
    } catch (e) {
      // 404 = jeszcze nie wyciągnięte — nie pokazujemy błędu
      if (e?.response?.status !== 404) toast.error(`BoQ: ${e?.response?.data?.detail || e.message}`);
    }
  }, [tenderId]);

  // Fetch threads
  const fetchThreads = useCallback(async () => {
    try {
      const data = await listRfqThreads({ opportunity_id: tenderId });
      setThreads(data?.items || []);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error(`Threads: ${e?.response?.data?.detail || e.message}`);
    }
  }, [tenderId]);

  // Initial load
  useEffect(() => {
    fetchBoQ();
    fetchThreads();
    getChannelCapabilities().then(setChannelCaps).catch(() => {});
  }, [fetchBoQ, fetchThreads]);

  // Plan outreach
  const handlePlanOutreach = async (threadId) => {
    setLoadingPlan(true);
    try {
      const plan = await planOutreach(threadId);
      setOutreachPlan({ threadId, plan });
    } catch (e) {
      toast.error(`Plan: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoadingPlan(false);
    }
  };

  const handleExecuteStep = async (threadId, stepNum) => {
    try {
      const r = await executeOutreachStep(threadId, stepNum);
      if (r.sent) {
        toast.success(`Krok ${stepNum} wysłany${r.dry_run ? ' (DRY)' : ''}`);
      } else {
        toast.error(`Krok ${stepNum}: ${r.error}`);
      }
      fetchThreads();
      // Odśwież plan
      handlePlanOutreach(threadId);
    } catch (e) {
      toast.error(`Execute: ${e.message}`);
    }
  };

  // Action: extract BoQ
  const handleExtractBoq = async () => {
    setBoqLoading(true);
    try {
      const r = await extractTenderBoq(tenderId);
      toast.success(`BoQ: ${r.items_extracted} pozycji wyciągniętych`);
      await fetchBoQ();
    } catch (e) {
      toast.error(`Extract failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBoqLoading(false);
    }
  };

  // Action: generate RFQ
  const handleGenerateRFQ = async () => {
    if (!boq) {
      toast.error('Najpierw wyciągnij BoQ');
      return;
    }
    setGenerating(true);
    try {
      const r = await generateTenderRfq(tenderId, { suppliers_per_category: 3 });
      toast.success(`Wygenerowano ${r.rfqs_generated} RFQ (${r.threads_created} nowych threadów)`);
      await fetchThreads();
    } catch (e) {
      toast.error(`RFQ gen failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Action: approve / send / pdf preview
  const handleApprove = async (threadId) => {
    try {
      await approveRfqThread(threadId, true);
      toast.success('Zatwierdzono');
      fetchThreads();
    } catch (e) { toast.error(`Approve: ${e.message}`); }
  };
  const handleSend = async (threadId) => {
    try {
      const r = await sendRfqThread(threadId);
      if (r.sent) toast.success(`Wysłano${r.dry_run ? ' (DRY RUN)' : ''}`);
      else toast.error(`Send failed: ${r.error}`);
      fetchThreads();
    } catch (e) { toast.error(`Send: ${e.message}`); }
  };
  const handlePreviewPDF = async (threadId) => {
    try {
      const url = getRfqThreadPdfUrl(threadId);
      window.open(url, '_blank');
    } catch (e) { toast.error(`PDF: ${e.message}`); }
  };
  const handlePollInbox = async () => {
    try {
      const r = await pollRfqInbox();
      toast.success(`Inbox: ${r.polled} maili, ${r.matched} dopasowanych${r.dry_run ? ' (DRY RUN — IMAP nie skonfigurowany)' : ''}`);
      fetchThreads();
    } catch (e) { toast.error(`Inbox: ${e.message}`); }
  };

  // Action: compute pricing chain (bid-mix → cost-stack → profitability)
  const handleComputePricing = async () => {
    setLoadingPricing(true);
    try {
      const mix = await getTenderBidMix(tenderId);
      if (mix.error) {
        toast.error(`Bid mix: ${mix.error}`);
        return;
      }
      setBidMix(mix);
      const cs = await getTenderCostStack(tenderId);
      setCostStack(cs);
      const pr = await getTenderProfitability(tenderId);
      setProfit(pr);
      toast.success(`Profitability: ${pr.profitability_score}/100 — ${pr.verdict}`);
    } catch (e) {
      toast.error(`Pricing chain: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoadingPricing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Workflow toolbar */}
      <Card className="border-dashed">
        <CardContent className="py-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleExtractBoq} disabled={boqLoading}>
            {boqLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            1. Wyciągnij BoQ
          </Button>
          <Button size="sm" variant="outline" onClick={handleGenerateRFQ} disabled={!boq || generating}>
            {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
            2. Generuj RFQ
          </Button>
          <Button size="sm" variant="outline" onClick={handlePollInbox}>
            <Inbox className="w-3 h-3 mr-1" /> 3. Sprawdź inbox
          </Button>
          <Button size="sm" onClick={handleComputePricing} disabled={loadingPricing}>
            {loadingPricing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Calculator className="w-3 h-3 mr-1" />}
            4. Oblicz Profitability
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            <FlaskConical className="w-3 h-3 inline mr-1" /> Pre-bid agent flow
          </div>
        </CardContent>
      </Card>

      {/* Hero */}
      <ProfitabilityHero profit={profit} loading={loadingPricing} onRefresh={handleComputePricing} />

      {/* 2-column: BidRecommendation + CostStackTable */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BidRecommendation costStack={costStack} loading={loadingPricing} onRecalc={handleComputePricing} />
        <CostStackTable costStack={costStack} loading={loadingPricing} onRefresh={handleComputePricing} />
      </div>

      {/* BoQ */}
      <BoQItemsTable boq={boq} bidMix={bidMix} loading={boqLoading} onExtract={handleExtractBoq} />

      {/* Multi-Channel Capabilities */}
      <ChannelsCapabilitiesCard caps={channelCaps} loading={!channelCaps} />

      {/* RFQ Threads */}
      <RFQThreadsTable
        threads={threads} loading={false}
        onApprove={handleApprove} onSend={handleSend}
        onPreviewPDF={handlePreviewPDF} onPollInbox={handlePollInbox}
        onPlanOutreach={handlePlanOutreach}
      />

      {/* Outreach Plan Modal */}
      {outreachPlan && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                Multi-Channel plan: {outreachPlan.plan.supplier_name}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setOutreachPlan(null)}>
                <XCircle className="w-3.5 h-3.5" />
              </Button>
            </div>
            <CardDescription className="text-xs">
              {outreachPlan.plan.tier_strategy?.label} ·
              Aktywne fazy: {outreachPlan.plan.active_phases?.join(', ')}
              {outreachPlan.plan.needs_approval && ' · ⚠️ Wymaga zatwierdzenia'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {outreachPlan.plan.steps.map((step, i) => {
              const ICONS = { email: '✉️', sms: '📱', whatsapp: '💬', voice: '☎️', linkedin: '💼' };
              const validBadge = step.address_valid ? '✓' : '✗ brak adresu';
              return (
                <div key={i} className="flex items-start gap-3 p-2 rounded bg-muted/30 border border-border/30">
                  <div className="text-xl">{ICONS[step.channel] || '?'}</div>
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium uppercase">{step.channel}</span>
                      <Badge variant="outline" className="text-[10px]">{step.type}</Badge>
                      <span className="text-muted-foreground">
                        +{step.delay_days}d
                      </span>
                      <span className={step.address_valid ? 'text-emerald-400' : 'text-red-400'}>
                        {validBadge}
                      </span>
                      {step.requires_approval && (
                        <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">approval</Badge>
                      )}
                    </div>
                    {step.address && (
                      <div className="text-muted-foreground mt-0.5 truncate">→ {step.address}</div>
                    )}
                    {step.payload_preview && (
                      <div className="mt-1 p-1.5 rounded bg-background/50 text-[11px] italic line-clamp-2 text-muted-foreground">
                        {step.payload_preview}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant={step.address_valid ? 'default' : 'outline'}
                    disabled={!step.address_valid}
                    className="h-7 shrink-0"
                    onClick={() => handleExecuteStep(outreachPlan.threadId, step.step_num)}
                  >
                    <Send className="w-3 h-3 mr-1" /> Wyślij
                  </Button>
                </div>
              );
            })}
            {(outreachPlan.plan.notes || []).length > 0 && (
              <div className="pt-2 border-t mt-3 text-xs text-amber-400 space-y-0.5">
                {outreachPlan.plan.notes.map((n, i) => (
                  <div key={i} className="flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 shrink-0" />
                    {n}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
